/**
 * One rule for what a data-backed surface may claim (UI-PRINCIPLES P3/P5):
 *
 * - while the request is in flight the state is "loading" — never "empty";
 * - when the request failed the state is "error" — never "empty", because an
 *   empty state invites the operator to create, connect or buy something that
 *   may already exist;
 * - "empty" is reserved for a request that succeeded and returned nothing
 *   (or, when the caller opts in, a 404 that genuinely means "none yet").
 *
 * Destructive or purchasing actions must only be offered in "ready" or
 * "empty" — never while the state is unknown ("loading" / "error").
 */
export type LoadState = "loading" | "error" | "empty" | "ready";

export interface QueryLike<T = unknown> {
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
  data?: T;
}

/** True when an API error is an HTTP 404 (the resource does not exist). */
export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  return (error as { status?: unknown }).status === 404;
}

export function resolveLoadState<T>(
  query: QueryLike<T>,
  isEmpty: (data: T | undefined) => boolean,
  options: { notFoundIsEmpty?: boolean } = {},
): LoadState {
  if (query.isLoading) return "loading";
  if (query.isError) {
    if (options.notFoundIsEmpty && isNotFoundError(query.error)) return "empty";
    // A failed background refetch keeps the last good data; that data is still real.
    if (query.data === undefined) return "error";
  }
  if (query.data === undefined && !query.isError) {
    // Disabled / idle query: nothing was asked, so nothing can be claimed as empty.
    return isEmpty(undefined) ? "empty" : "ready";
  }
  return isEmpty(query.data) ? "empty" : "ready";
}

/** True when it is unknown whether data exists — gate creating/buying/overwriting actions on this. */
export function isStateUnknown(state: LoadState): boolean {
  return state === "loading" || state === "error";
}
